<template>
  <v-dialog :value="visible" max-width="600px" @input="updateVisible">
    <v-card>
      <v-card-title class="headline">
        <slot name="title">{{ title }}</slot>
      </v-card-title>
      <v-card-text>
        <v-textarea
          v-model="localContent"
          :label="label"
          :rules="rules"
          :auto-grow="false"
          :min-rows="minRows"
          :max-rows="maxRows"
          ref="memoModalTextarea"
          class="memo-modal-textarea"
          :placeholder="placeholder"
          @input="debouncedAdjustSize"
          @focus="adjustMemoModalSize"
          @compositionend="adjustMemoModalSize"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="onCancel">{{ cancelText }}</v-btn>
        <v-btn color="blue darken-1" text @click="onConfirm">{{ confirmText }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'MemoEditorDialog',
  props: {
    value: { type: String, default: '' },
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    title: { type: String, default: 'メモ編集' },
    label: { type: String, default: 'メモ' },
    placeholder: { type: String, default: 'メモを入力してください...' },
    minRows: { type: Number, default: 5 },
    maxRows: { type: Number, default: 15 },
    confirmText: { type: String, default: '登録' },
    cancelText: { type: String, default: 'キャンセル' },
  },
  data() {
    return {
      localContent: this.value,
      adjustSizeTimeout: null,
    };
  },
  watch: {
    value(newVal) {
      this.localContent = newVal || '';
    },
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.memoModalTextarea && this.$refs.memoModalTextarea.$el) {
            this.$refs.memoModalTextarea.focus();
            this.adjustMemoModalSize();
          }
        });
      }
    },
  },
  methods: {
    updateVisible(val) {
      this.$emit('update:visible', val);
    },
    onConfirm() {
      this.$emit('input', this.localContent);
      this.$emit('update:visible', false);
      this.$emit('confirm', this.localContent);
    },
    onCancel() {
      this.$emit('update:visible', false);
      this.$emit('cancel');
    },
    debouncedAdjustSize() {
      // Clear existing timeout
      if (this.adjustSizeTimeout) {
        clearTimeout(this.adjustSizeTimeout);
      }
      
      // Set new timeout to debounce the adjustment
      this.adjustSizeTimeout = setTimeout(() => {
        this.adjustMemoModalSize();
      }, 100);
    },
    adjustMemoModalSize() {
      this.$nextTick(() => {
        const textarea = this.$refs.memoModalTextarea && this.$refs.memoModalTextarea.$el
          ? this.$refs.memoModalTextarea.$el.querySelector('textarea')
          : null;
        if (!textarea) return;

        const MIN_HEIGHT = 120;
        const MAX_HEIGHT = 360;

        // Store current scroll position
        const scrollTop = textarea.scrollTop;
        
        // Reset height to auto to get accurate scrollHeight
        textarea.style.height = 'auto';
        
        // Calculate new height
        const scrollHeight = textarea.scrollHeight;
        const newHeight = Math.max(MIN_HEIGHT, Math.min(scrollHeight, MAX_HEIGHT));
        
        // Only update if height actually changed to prevent flickering
        if (Math.abs(parseInt(textarea.style.height) - newHeight) > 1) {
          textarea.style.height = newHeight + 'px';
        }
        
        // Restore scroll position
        textarea.scrollTop = scrollTop;
      });
    },
  },
  beforeDestroy() {
    // Clean up timeout when component is destroyed
    if (this.adjustSizeTimeout) {
      clearTimeout(this.adjustSizeTimeout);
    }
  },
};
</script>

<style scoped>
.memo-modal-textarea ::v-deep .v-text-field__slot textarea {
  min-height: 120px !important;
  max-height: unset !important;
  line-height: 24px !important;
  padding: 12px !important;
  font-size: 14px !important;
  overflow-y: auto !important;
  resize: vertical !important;
}
</style>


